import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import * as purifyModule from 'dompurify';
import { NotificationUtilityService } from '../../services/notification-utility.service';

// Card #676: DOMPurify 3's ES build (what the app bundle loads) has ONLY a default export, so
// `import * as` alone leaves .sanitize undefined there; jest loads the CommonJS build, where the
// namespace IS the instance and a default import is undefined. Use whichever one can sanitize.
type PurifyLike = { sanitize(dirty: string, config?: object): string };
export function resolveDomPurify(mod: unknown): PurifyLike | null {
  const m = mod as { default?: unknown } | null;
  for (const candidate of [m?.default, m]) {
    if (candidate && typeof (candidate as PurifyLike).sanitize === 'function') return candidate as PurifyLike;
  }
  return null;
}
const DOMPurify = resolveDomPurify(purifyModule);
if (!DOMPurify) {
  // Logged once at load so blank notifications have a visible cause.
  console.error('[NotificationSafeHtmlPipe] DOMPurify could not be loaded; notifications will render empty.');
}

/**
 * Pipe to safely transform notification descriptions into localized HTML.
 * Moves the security-sensitive bypassSecurityTrustHtml call from services to a Pipe,
 * which is the standard Angular pattern for handling dynamic HTML content.
 */
@Pipe({
    name: 'notificationSafeHtml',
    pure: false // Necessary to react to language changes as it depends on TranslateService
    ,
    standalone: false
})
export class NotificationSafeHtmlPipe implements PipeTransform {
  constructor(
    private sanitizer: DomSanitizer,
    private translateService: TranslateService,
    private notificationUtility: NotificationUtilityService
  ) {}

  // The pipe is impure, so Angular calls transform() on EVERY change detection, for every
  // notification in the header (the bell's list is in the DOM on every page, open or not).
  // Each call built and ran one RegExp per COMMON key (~800), for each notification. On the
  // agenda (card #391) that was 2.1-2.6 s of the ~4 s of JavaScript a calendar load ran.
  // Recompute only when something the result depends on changes: the text, the language,
  // or the loaded translations (a new key list or translation table means a reload).
  // getSortedCommonKeys() returns one array until updateTranslationCache() replaces it, and it
  // replaces it every time it rebuilds the translation map (pinned in the spec). instant()
  // falls back to the default language, so that table is part of the key too.
  private lastValue: string | undefined;
  private lastLang: string | undefined;
  private lastKeys: string[] | undefined;
  private lastTranslations: any;
  private lastDefaultTranslations: any;
  private lastResult: SafeHtml | undefined;

  transform(value: string): SafeHtml {
    const lang = this.translateService.currentLang;
    const keys = this.notificationUtility.getSortedCommonKeys();
    const translations = this.translateService.translations?.[lang];
    const defaultTranslations = this.translateService.translations?.[this.translateService.defaultLang];
    if (
      this.lastResult !== undefined &&
      value === this.lastValue &&
      lang === this.lastLang &&
      keys === this.lastKeys &&
      translations === this.lastTranslations &&
      defaultTranslations === this.lastDefaultTranslations
    ) {
      return this.lastResult;
    }
    const result = this.compute(value);
    this.lastValue = value;
    this.lastLang = lang;
    this.lastKeys = keys;
    this.lastTranslations = translations;
    this.lastDefaultTranslations = defaultTranslations;
    this.lastResult = result;
    return result;
  }

  private compute(value: string): SafeHtml {
    if (!value || typeof value !== 'string') {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    // Preliminary cleanup of raw notification string
    let translation = value.replace(/[{}]/g, ' ').replace(/\s+/g, ' ').trim();

    try {
      // 1. Handle specialized "Click here" patterns with baked-in links
      if (translation.includes('<a')) {
        let splitText = translation.split('<a');
        let beforeAnchor = splitText[0];
        let anchorPart = '<a' + (splitText[1] || '');

        const hrefMatch = anchorPart.match(/href="([^"]*)"/i);
        const href = hrefMatch ? hrefMatch[1] : '#';

        // Transform "Click" into a styled link if present in prefix
        if (/\bClick\b/i.test(beforeAnchor)) {
          beforeAnchor = beforeAnchor.replace(
            /\bClick\b/i,
            `<a href="${href}" target="_blank" download>Click</a>`
          );
          anchorPart = '';
        } else {
          // Normalize "here" links
          anchorPart = anchorPart.replace(/(<a[^>]*>)\s*here\s*(<\/a>)/i, '$1Click$2');
        }

        // Translate the prefix text specifically
        const translatedBefore = this.getTranslatedText("COMMON." + beforeAnchor.trim()) || beforeAnchor.trim();
        translation = translatedBefore + ' ' + anchorPart;
      } else {
        // 2. Handle simple keyword replacement for standard notifications
        const sortedKeys = this.notificationUtility.getSortedCommonKeys();
        const translationMap = this.notificationUtility.getCachedTranslationMap();

        sortedKeys.forEach(key => {
          const translated = translationMap.get(key);
          if (!translated) return;
          
          const escapedKey = key.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
          const regex = new RegExp(`(^|[^a-zA-Z0-9])${escapedKey}(?=[^a-zA-Z0-9]|$)`, 'gi');
          translation = translation.replace(regex, (_, p1) => { return p1 + translated; });
        });
      }
    } catch (e) {
      // Fallback: Return empty string on logic failure to avoid leaking unsanitized content
      translation = '';
    }

    // Final Sanitization Step: 
    // We sanitize the result of all string manipulations/translations using a strict whitelist.
    // Fail closed: without a working sanitizer nothing is marked as trusted HTML.
    if (!DOMPurify) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }
    // A stray '%' makes decodeURIComponent throw (URIError), which broke the whole list; the
    // undecoded text is still sanitized below.
    let decodedHtml: string;
    try {
      decodedHtml = decodeURIComponent(translation || '');
    } catch {
      decodedHtml = translation || '';
    }
    const sanitizedHtml = DOMPurify.sanitize(decodedHtml, {
      ALLOWED_TAGS: ['a', 'b', 'strong', 'i', 'em', 'span', 'br', 'p'],
      ALLOWED_ATTR: ['href', 'target', 'download', 'class'],
      KEEP_CONTENT: true
    });

    /**
     * JUSTIFICATION: Content has been strictly sanitized via DOMPurify with 
     * a restricted whitelist. Essential for rendering product-required 
     * interactive links in notifications.
     */
    return this.sanitizer.bypassSecurityTrustHtml(sanitizedHtml);
  }

  private getTranslatedText(key: string): string {
    const translation = this.translateService.instant(key);
    return translation !== key ? translation : '';
  }
}
